import { useMemo, useState } from 'react';
import UndoIcon from '@mui/icons-material/Undo';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Typography,
} from '@mui/material';
import { formatLocalDateTime } from '../Shared/datetime';
import { revokeBidAcceptance } from '../Shared/publicApi';

const AcceptanceRevocationPanel = ({
	orgName,
	acceptedAt,
	acceptanceRevokedAt,
	canRevoke,
	acceptanceFinalized,
	revocationDeadline,
	token,
	bidId,
	variant = 'accepted',
	onRevoked,
}) => {
	const [revoking, setRevoking] = useState(false);
	const [revokeError, setRevokeError] = useState('');

	const acceptedAtLabel = useMemo(
		() => formatLocalDateTime(acceptedAt),
		[acceptedAt],
	);
	const revokedAtLabel = useMemo(
		() => formatLocalDateTime(acceptanceRevokedAt),
		[acceptanceRevokedAt],
	);
	const revocationDeadlineLabel = useMemo(
		() => formatLocalDateTime(revocationDeadline),
		[revocationDeadline],
	);

	const deadlinePassed = useMemo(() => {
		if (!revocationDeadline) {
			return false;
		}
		return new Date(revocationDeadline).getTime() <= Date.now();
	}, [revocationDeadline]);

	const showAcceptanceInfo =
		Boolean(acceptedAtLabel) && !acceptanceRevokedAt && orgName;
	const showRevokeAction = Boolean(
		canRevoke && token && revocationDeadlineLabel && !deadlinePassed,
	);

	const bindingNotice = useMemo(() => {
		if (!orgName) {
			return null;
		}
		if (acceptanceRevokedAt && revokedAtLabel) {
			return null;
		}
		if (variant === 'status' && showAcceptanceInfo) {
			if (canRevoke && revocationDeadlineLabel && !deadlinePassed) {
				return `You accepted this proposal and authorized ${orgName} to proceed with the work described. You can cancel until ${revocationDeadlineLabel}; after that, this acceptance becomes a binding agreement.`;
			}
			if (acceptanceFinalized) {
				return `Your acceptance of this proposal is a binding agreement with ${orgName} for the work described.`;
			}
			return null;
		}
		if (variant === 'accepted' && showAcceptanceInfo) {
			if (canRevoke && revocationDeadlineLabel && !deadlinePassed) {
				return `By clicking Accept, you agreed to the terms of this proposal and authorized ${orgName} to proceed with the work described. If you take no further action before ${revocationDeadlineLabel}, this acceptance becomes a binding agreement.`;
			}
			if (acceptanceFinalized) {
				return `Your acceptance of this proposal is a binding agreement with ${orgName} for the work described.`;
			}
		}
		return null;
	}, [
		orgName,
		acceptanceRevokedAt,
		revokedAtLabel,
		variant,
		showAcceptanceInfo,
		canRevoke,
		revocationDeadlineLabel,
		deadlinePassed,
		acceptanceFinalized,
	]);

	const handleRevoke = async () => {
		if (!token) {
			return;
		}
		setRevoking(true);
		setRevokeError('');
		try {
			await revokeBidAcceptance({ token, bidId });
			onRevoked?.();
		} catch (error) {
			setRevokeError(
				error.message ||
					'Could not cancel acceptance. Please contact the contractor.',
			);
		} finally {
			setRevoking(false);
		}
	};

	if (!showAcceptanceInfo && !revokedAtLabel) {
		return null;
	}

	return (
		<Box sx={{ textAlign: 'left', mt: 2 }}>
			{acceptedAtLabel && showAcceptanceInfo && (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Accepted {acceptedAtLabel}
				</Typography>
			)}
			{revokedAtLabel && (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Acceptance cancelled {revokedAtLabel}
				</Typography>
			)}

			{bindingNotice && (
				<Alert severity="info" sx={{ mb: showRevokeAction ? 2 : 0 }}>
					{bindingNotice}
				</Alert>
			)}

			{showRevokeAction && (
				<Box>
					<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
						Changed your mind?
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						You can cancel this acceptance until {revocationDeadlineLabel}.
					</Typography>
					{revokeError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{revokeError}
						</Alert>
					)}
					<Button
						variant="outlined"
						color="inherit"
						startIcon={
							revoking ? (
								<CircularProgress size={18} color="inherit" />
							) : (
								<UndoIcon />
							)
						}
						onClick={handleRevoke}
						disabled={revoking}
						sx={{ width: { xs: '100%', sm: 'auto' } }}
					>
						Cancel acceptance
					</Button>
				</Box>
			)}
		</Box>
	);
};

export default AcceptanceRevocationPanel;
